import React, { useCallback, useState, useSyncExternalStore } from 'react';

import { logError } from '../../../lib/logger';
import {
  BrowserPushPermissionDenied,
  browserPushSupported,
  getOneSignalState,
  optInBrowserPush,
  optOutBrowserPush,
  subscribeOneSignalState,
} from '../../../lib/onesignal';

import { SettingsGroup, SettingsToggle } from '../../shared/SettingsList';

// Per-browser push enrollment — account-scoped even though it renders in the
// workspace Notifications panel (the subtitle says so). Opting in subscribes
// this browser for the whole account; each workspace's notification
// notification settings still decide when that space pushes. The SDK only loads on
// first interaction (or at SPA boot for already-enrolled browsers), so
// rendering this toggle costs nothing. Hidden on mobile web (no push without
// an installed PWA); renders nothing in the native shells, which register
// push natively.
const BrowserPushToggle = () => {
  const pushState = useSyncExternalStore(subscribeOneSignalState, getOneSignalState);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = useCallback(async (nextChecked) => {
    setIsBusy(true);
    setError(null);

    try {
      if (nextChecked) await optInBrowserPush();
      else await optOutBrowserPush();
    } catch (toggleError) {
      if (toggleError instanceof BrowserPushPermissionDenied) {
        setError("Notifications were blocked. Allow them in your browser's site settings to get notified here.");
      } else {
        setError('Unable to update browser notifications.');
        logError('BrowserPushToggle', 'Failed to toggle browser push', { error: toggleError });
      }
    } finally {
      setIsBusy(false);
    }
  }, []);

  if (!browserPushSupported()) return null;

  const denied = pushState.permission === 'denied';
  const description =
    error ||
    (denied
      ? "Notifications are blocked in your browser's settings."
      : 'This will turn it on for all chats, including this one.');

  return (
    <SettingsGroup className="mt-3 hidden md:flex">
      <SettingsToggle
        label="Get notified in this browser"
        description={description}
        checked={pushState.optedIn && pushState.permission === 'granted'}
        disabled={isBusy || denied}
        onChange={handleChange}
      />
    </SettingsGroup>
  );
};

export default BrowserPushToggle;
